import {
    QUICK_FILTERS,
    DISCLOSURE_PRODUCT_TYPE_LABEL,
    PITCH_PRODUCT_TYPE_LABEL,
    IIR_PRODUCT_TYPE_LABEL,
} from '../../constants/quickFilters';

export const ALL_PRODUCTS_FILTER_FIELDS = [
    { key: 'productType.label', label: 'Product Type' },
    { key: 'status', label: 'Status' },
    { key: 'userDisplayName', label: 'Owner' },
];

export const DEFAULT_ROWS = ALL_PRODUCTS_FILTER_FIELDS.map(({ key }) => ({
    propertyKey: key,
    operator: '=',
    values: [],
}));

// O(1) membership test — avoids scanning QUICK_FILTERS on every resolveFilter call
export const QUICK_FILTER_VALUES = new Set(Object.values(QUICK_FILTERS));

/** Product-type values for SITREP products. */
export const SITREP_PRODUCT_TYPE_LABELS = ['Tactical SITREP', 'Strategic SITREP'];

/**
 * Maps a quick-filter key to filter-bar rows.
 * ponytail: author match uses displayName — covers primary author; co-authorship via
 * authorUids is not surfaced by the string filter.
 */
export function quickFilterToRows(key, userDisplayName) {
    // Flexible row builder. `productType` optionally constrains the product-type row with a
    // given operator (defaults to '=') — used by FINTEL (!= Disclosure) and Disclosures (= Disclosure).
    const build = ({ status, author, productType, productTypeOp = '=' } = {}) =>
        DEFAULT_ROWS.map((r) => {
            if (r.propertyKey === 'status' && status) return { ...r, values: [status] };
            if (r.propertyKey === 'userDisplayName' && author) return { ...r, values: [author] };
            if (r.propertyKey === 'productType.label' && productType) {
                return { ...r, operator: productTypeOp, values: [productType] };
            }
            return r;
        });

    switch (key) {
        // Org-wide all drafts — no author constraint so shared drafts are visible.
        case QUICK_FILTERS.ALL_DRAFTS: return build({ status: 'Draft' });
        case QUICK_FILTERS.MY_DRAFTS: return build({ status: 'Draft', author: userDisplayName });
        case QUICK_FILTERS.MY_UNDER_REVIEW: return build({ status: 'Awaiting Review', author: userDisplayName });
        case QUICK_FILTERS.MY_APPROVED: return build({ status: 'approved', author: userDisplayName });
        case QUICK_FILTERS.PITCHES:
            // Org-wide pitch products (no author filter); status left open.
            return build({
                productType: PITCH_PRODUCT_TYPE_LABEL,
            });
        case QUICK_FILTERS.FINTEL:
            // Org-wide approved products that are NOT disclosures, NOT pitches and NOT
            // IIRs (IIRs are raw collection data, not Intelligence Products — P10).
            // Uses multi-value != to exclude the product types.
            return DEFAULT_ROWS.map((r) => {
                if (r.propertyKey === 'status') return { ...r, values: ['approved'] };
                if (r.propertyKey === 'productType.label') {
                    return {
                        ...r,
                        operator: '!=',
                        values: [DISCLOSURE_PRODUCT_TYPE_LABEL, PITCH_PRODUCT_TYPE_LABEL, IIR_PRODUCT_TYPE_LABEL],
                    };
                }
                return r;
            });
        case QUICK_FILTERS.SITREP:
            // Org-wide SITREP products (tactical + strategic); status left open.
            return DEFAULT_ROWS.map((r) => {
                if (r.propertyKey === 'productType.label') {
                    return { ...r, operator: '=', values: SITREP_PRODUCT_TYPE_LABELS };
                }
                return r;
            });
        case QUICK_FILTERS.DISCLOSURES:
            // Org-wide disclosure products (no author filter); status left open so both
            // draft and approved disclosures are visible.
            return build({
                productType: DISCLOSURE_PRODUCT_TYPE_LABEL,
            });
        case QUICK_FILTERS.ALL_PUBLISHED: return build({ status: 'approved' });
        default: return DEFAULT_ROWS;
    }
}

/**
 * Resolves the active filter key and its corresponding rows from the URL param.
 * Pure — no side effects, safe to call during render.
 * Returns QUICK_FILTERS.ALL_DRAFTS when the param references a saved filter that can't be found.
 */
export function resolveFilter(param, savedFilters, userDisplayName) {
    if (!param) {
        return { activeQuickFilter: QUICK_FILTERS.ALL_DRAFTS, rows: quickFilterToRows(QUICK_FILTERS.ALL_DRAFTS, userDisplayName) };
    }
    if (QUICK_FILTER_VALUES.has(param)) {
        return { activeQuickFilter: param, rows: quickFilterToRows(param, userDisplayName) };
    }
    if (param.startsWith('saved:')) {
        const id = param.slice(6);
        const sf = savedFilters.find((f) => f.id === id);
        if (sf) return { activeQuickFilter: `saved:${sf.id}`, rows: sf.rows };
    }
    return { activeQuickFilter: QUICK_FILTERS.ALL_DRAFTS, rows: quickFilterToRows(QUICK_FILTERS.ALL_DRAFTS, userDisplayName) };
}

/** Order-insensitive row equality. */
export function rowsEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((ra, i) => {
        const rb = b[i];
        return ra.propertyKey === rb.propertyKey &&
            ra.operator === rb.operator &&
            JSON.stringify([...ra.values].sort()) === JSON.stringify([...rb.values].sort());
    });
}
